import { useMemo } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { CITY_COORDS } from "@/data/cityCoords";
import { ZONE_COLORS } from "@/components/ZoneMap";

const makeDot = (color, size = 12) =>
    L.divIcon({
        className: "city-dot",
        html: `<span style="display:block;width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:2px solid #ffffff;box-shadow:0 1px 4px rgba(10,15,36,0.4)"></span>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });

const hqIcon = L.divIcon({
    className: "city-hq",
    html: `<span style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:9999px;background:#0F172A;border:3px solid #ffffff;box-shadow:0 2px 8px rgba(10,15,36,0.5)"><span style="width:7px;height:7px;border-radius:9999px;background:#ffffff"></span></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
});

const CityMap = ({ city, others = [] }) => {
    const center = CITY_COORDS[city.slug];
    const hq = CITY_COORDS["mont-dol"];
    const mainIcon = useMemo(() => makeDot(ZONE_COLORS[city.zone] || "#1B43BD", 18), [city.zone]);
    const otherIcons = useMemo(
        () => Object.fromEntries(Object.entries(ZONE_COLORS).map(([k, v]) => [k, makeDot(v, 11)])),
        []
    );
    const markers = useMemo(() => others.filter((c) => CITY_COORDS[c.slug]), [others]);
    const bounds = useMemo(
        () => L.latLngBounds([center, hq, ...markers.map((c) => CITY_COORDS[c.slug])]).pad(0.12),
        [center, hq, markers]
    );

    if (!center) return null;

    return (
        <div data-testid="city-map" className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_20px_60px_rgba(10,15,36,0.12)]">
            <MapContainer
                bounds={bounds}
                scrollWheelZoom={false}
                zoomControl={false}
                className="h-[320px] w-full md:h-[380px]"
                style={{ background: "#F3F6FD" }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={hq} icon={hqIcon}>
                    <Popup>
                        <div className="text-sm">
                            <p className="font-bold text-[#1B43BD]">Base Eradikbreizh : Le Mont-Dol</p>
                            <p>à {city.dist} de {city.name} · intervention sous 2h, 7j/7.</p>
                        </div>
                    </Popup>
                </Marker>
                <Marker position={center} icon={mainIcon}>
                    <Popup>
                        <div className="text-sm">
                            <p className="font-bold">{city.name}</p>
                            <p className="text-slate-500">Votre ville · intervention sous 2h, 7j/7</p>
                        </div>
                    </Popup>
                </Marker>
                {markers.map((c) => (
                    <Marker key={c.slug} position={CITY_COORDS[c.slug]} icon={otherIcons[c.zone]}>
                        <Popup>
                            <div className="min-w-[160px] text-sm">
                                <p className="font-bold">{c.name}</p>
                                <Link to={`/punaises-de-lit/${c.slug}`} className="mt-1 inline-block font-semibold text-[#1B43BD] underline">
                                    Punaises de lit à {c.name}
                                </Link>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default CityMap;
